import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  FileSpreadsheet,
  FileText,
  Database,
  ArrowUpDown,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Check,
  Loader2,
  Search,
  CheckSquare,
  Square,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';

export type ColumnItem =
  | string
  | {
    key?: string;
    field?: string;
    id?: string;
    name?: string;
    label?: string;
    title?: string;
  };

export interface NormalizedColumn {
  key: string;
  label: string;
}

export interface DBExportModalProps {
  /** API endpoint to trigger export */
  endpoint: string;
  /**
   * Columns to include and sort by.
   * Can be string array (['database', 'tables']), comma-separated string ('database,tables'),
   * or objects array ([{ key: 'database', label: 'Database' }]).
   * Optional when isdatabase is true.
   */
  columns?: ColumnItem[] | string;
  /** Database Cluster ID (e.g. pg-7693755722357090997) */
  cluster_id?: string | number;
  clusterId?: string | number;
  /** Client ID (e.g. 94) */
  client_id?: string | number;
  clientId?: string | number;
  /** Initial column to sort by. Defaults to the first column or 'tables' if available */
  defaultSortBy?: string;
  /** Initial sort order: 'desc' (default) or 'asc' */
  defaultSortOrder?: 'asc' | 'desc';
  /** Export type param, defaults to 'excel' */
  exportType?: string;
  /** Optional file name for downloaded file */
  fileName?: string;
  /** Additional query parameters to pass to the endpoint */
  extraParams?: Record<string, any>;

  // Database-level & Table-level export mode
  /** If true, modal runs in database export mode (hides column picker and sort, only shows format picker) */
  isdatabase?: boolean;
  isDatabase?: boolean;
  isdatabse?: boolean;
  /** Database name to export (e.g. 'ticketing') */
  databasename?: string;
  databaseName?: string;
  database?: string;
  name?: string;
  /** Table name to export (e.g. 'users') */
  tableName?: string;
  table?: string;
  table_name?: string;
  /** Schema name (e.g. 'public') */
  schemaName?: string;
  schema?: string;

  // UI Customizations
  /** Button label, default: 'Export' */
  buttonText?: string;
  /** Custom class name for the trigger button */
  buttonClassName?: string;
  /** Custom icon for the trigger button */
  buttonIcon?: React.ReactNode;
  /** Custom modal width class, defaults to 'max-w-2xl' */
  maxWidth?: string;
  /** Whether to render the export button. Set to false if controlling modal externally */
  showButton?: boolean;
  /** Controlled open state */
  isOpen?: boolean;
  /** Controlled close callback */
  onClose?: () => void;
  /** Modal title */
  title?: string;
  /** Modal subtitle */
  subtitle?: string;
  /** Callback on successful export */
  onSuccess?: () => void;
  /** Callback on failed export */
  onError?: (err: any) => void;
  /** Disabled state for the export button */
  disabled?: boolean;
}

/**
 * Normalizes different column formats into a clean { key, label } array
 */
function normalizeColumns(rawColumns: ColumnItem[] | string): NormalizedColumn[] {
  if (!rawColumns) return [];

  let items: any[] = [];
  if (typeof rawColumns === 'string') {
    items = rawColumns
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (Array.isArray(rawColumns)) {
    items = rawColumns;
  }

  return items
    .map((item): NormalizedColumn | null => {
      if (typeof item === 'string') {
        const key = item.trim();
        if (!key) return null;
        const label = key
          .replace(/[_-]/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        return { key, label };
      }
      if (typeof item === 'object' && item !== null) {
        const key = item.key || item.field || item.id || item.name || '';
        if (!key) return null;
        const label =
          item.label ||
          item.title ||
          key
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, (c: any) => c.toUpperCase());
        return { key, label };
      }
      return null;
    })
    .filter((col): col is NormalizedColumn => Boolean(col && col.key));
}

export const DBExportModal: React.FC<DBExportModalProps> = ({
  endpoint,
  columns = [],
  cluster_id,
  clusterId,
  client_id,
  clientId,
  defaultSortBy,
  defaultSortOrder = 'desc',
  exportType = 'excel',
  fileName,
  extraParams,
  isdatabase,
  isDatabase,
  isdatabse,
  databasename,
  databaseName,
  database,
  name,
  tableName,
  table,
  table_name,
  schemaName,
  schema,
  buttonText = 'Export',
  buttonClassName,
  buttonIcon,
  maxWidth,
  showButton = true,
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  title = 'Export Data',
  subtitle,
  onSuccess,
  onError,
  disabled = false,
}) => {
  // Database / Table-level mode
  const isDatabaseMode = Boolean(isdatabase ?? isDatabase ?? isdatabse);
  const effectiveDatabaseName = databasename ?? databaseName ?? database ?? (isDatabaseMode ? name : '') ?? '';
  const effectiveTableName = tableName ?? table ?? table_name ?? (!isDatabaseMode ? name : '') ?? '';
  const effectiveSchemaName = schema ?? schemaName ?? '';
  const isFormatOnlyMode =
    isDatabaseMode ||
    (Boolean(effectiveTableName) && (!columns || (Array.isArray(columns) && columns.length === 0)));

  // Selected format: 'excel' or 'csv'
  const [selectedFormat, setSelectedFormat] = useState<string>(() =>
    exportType === 'csv' ? 'csv' : 'excel'
  );

  useEffect(() => {
    if (exportType) {
      setSelectedFormat(exportType === 'csv' ? 'csv' : 'excel');
    }
  }, [exportType]);

  const effectiveMaxWidth = maxWidth || (isFormatOnlyMode ? 'max-w-md' : 'max-w-2xl');
  const modalTitle =
    title !== 'Export Data'
      ? title
      : effectiveTableName
        ? `Export ${effectiveTableName}`
        : isDatabaseMode && effectiveDatabaseName
          ? `Export ${effectiveDatabaseName}`
          : 'Export Data';
  const modalSubtitle =
    subtitle ||
    (isFormatOnlyMode
      ? effectiveTableName
        ? 'Choose format to export table details'
        : 'Choose format to export database data'
      : 'Select export columns and format preferences');

  // Normalize columns
  const normalizedColumns = useMemo(() => normalizeColumns(columns), [columns]);

  // Internal modal visibility state (when uncontrolled)
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isModalOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // Selected column to sort by
  const [sortBy, setSortBy] = useState<string>(() => {
    if (defaultSortBy && normalizedColumns.some((c) => c.key === defaultSortBy)) {
      return defaultSortBy;
    }
    const tableCol = normalizedColumns.find(
      (c) => c.key.toLowerCase() === 'tables' || c.key.toLowerCase() === 'table'
    );
    if (tableCol) return tableCol.key;
    return normalizedColumns[0]?.key || '';
  });

  // Selected sort order: 'asc' or 'desc'
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);

  // Selected columns to export
  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(() =>
    normalizedColumns.map((c) => c.key)
  );

  // Search filter for columns
  const [columnSearch, setColumnSearch] = useState('');

  // Loading state during export
  const [isExporting, setIsExporting] = useState(false);

  // Effective cluster ID & client ID
  const effectiveClusterId = cluster_id ?? clusterId ?? '';
  const effectiveClientId = client_id ?? clientId ?? '';

  // Sync state when columns or default values change
  useEffect(() => {
    if (normalizedColumns.length > 0) {
      if (!sortBy || !normalizedColumns.some((c) => c.key === sortBy)) {
        const tableCol = normalizedColumns.find(
          (c) => c.key.toLowerCase() === 'tables' || c.key.toLowerCase() === 'table'
        );
        setSortBy(tableCol ? tableCol.key : normalizedColumns[0].key);
      }
      setSelectedExportColumns((prev) => {
        const validKeys = normalizedColumns.map((c) => c.key);
        const filtered = prev.filter((k) => validKeys.includes(k));
        return filtered.length > 0 ? filtered : validKeys;
      });
    }
  }, [normalizedColumns, sortBy]);

  const handleOpenModal = () => {
    if (disabled) return;
    setInternalIsOpen(true);
  };

  const handleCloseModal = () => {
    setInternalIsOpen(false);
    controlledOnClose?.();
  };

  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) {
          showError('At least one column must be selected');
          return prev;
        }
        const updated = prev.filter((k) => k !== key);
        if (sortBy === key && updated.length > 0) {
          setSortBy(updated[0]);
        }
        return updated;
      } else {
        return [...prev, key];
      }
    });
  };

  const selectAllColumns = () => {
    setSelectedExportColumns(normalizedColumns.map((c) => c.key));
  };

  // Ensure sortBy is always one of the selected export columns
  useEffect(() => {
    if (selectedExportColumns.length > 0 && !selectedExportColumns.includes(sortBy)) {
      setSortBy(selectedExportColumns[0]);
    }
  }, [selectedExportColumns, sortBy]);

  // Available columns for "Sort By" - strictly filtered to only columns selected in Export Columns
  const selectableSortColumns = useMemo(() => {
    const selected = normalizedColumns.filter((c) => selectedExportColumns.includes(c.key));
    if (!columnSearch.trim()) return selected;
    const query = columnSearch.toLowerCase().trim();
    return selected.filter(
      (c) => c.key.toLowerCase().includes(query) || c.label.toLowerCase().includes(query)
    );
  }, [normalizedColumns, selectedExportColumns, columnSearch]);

  // Construct query parameters
  const exportParams = useMemo(() => {
    const params: Record<string, any> = {
      ...(effectiveClusterId ? { cluster_id: effectiveClusterId } : {}),
      ...(effectiveClientId ? { client_id: effectiveClientId } : {}),
      export: selectedFormat,
      ...(extraParams || {}),
    };

    if (isDatabaseMode) {
      if (effectiveDatabaseName) {
        params.database = effectiveDatabaseName;
      }
    } else {
      if (effectiveDatabaseName) {
        params.database = effectiveDatabaseName;
      }
      if (effectiveSchemaName) {
        params.schema = effectiveSchemaName;
      }
      if (effectiveTableName) {
        params.table = effectiveTableName;
        params.table_name = effectiveTableName;
      }

      if (!isFormatOnlyMode && normalizedColumns.length > 0) {
        const columnsParam =
          selectedExportColumns.length > 0
            ? selectedExportColumns.join(',')
            : normalizedColumns.map((c) => c.key).join(',');

        params.sort_by = sortBy || (normalizedColumns[0]?.key ?? '');
        params.sort_order = sortOrder;
        params.columns = columnsParam;
      }
    }

    return params;
  }, [
    effectiveClusterId,
    effectiveClientId,
    selectedFormat,
    isDatabaseMode,
    isFormatOnlyMode,
    effectiveDatabaseName,
    effectiveSchemaName,
    effectiveTableName,
    sortBy,
    sortOrder,
    selectedExportColumns,
    normalizedColumns,
    extraParams,
  ]);

  // Execute export API call and trigger file download
  const handleExport = async () => {
    if (!endpoint) {
      showError('Export endpoint is required');
      return;
    }

    setIsExporting(true);
    try {
      const response = await baseapi.get(endpoint, { params: exportParams, responseType: 'blob' });

      if (response && response.data) {
        // Verify if returned blob is actually a JSON error payload
        if (response.data instanceof Blob) {
          const contentType = response.headers?.['content-type'];
          if (contentType && contentType.includes('application/json')) {
            const text = await response.data.text();
            try {
              const jsonData = JSON.parse(text);
              if (jsonData.status === false || jsonData.error || jsonData.message) {
                showError(jsonData.message || jsonData.error || 'Export failed');
                onError?.(jsonData);
                return;
              }
            } catch {
              // Not JSON, continue to download
            }
          }

          // Determine download file name
          let downloadName = fileName;
          if (!downloadName) {
            const disposition = response.headers?.['content-disposition'];
            if (disposition && disposition.includes('filename=')) {
              const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match && match[1]) {
                downloadName = match[1].replace(/['"]/g, '').trim();
              }
            }
          }

          if (!downloadName) {
            const cleanEndpoint =
              endpoint.split('?')[0].split('/').filter(Boolean).pop() || 'db_monitor';
            const baseName =
              effectiveTableName || (isDatabaseMode && effectiveDatabaseName ? effectiveDatabaseName : cleanEndpoint);
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '_');
            const ext = selectedFormat === 'csv' ? 'csv' : 'xlsx';
            downloadName = `${baseName}_export_${timestamp}.${ext}`;
          }

          // Trigger browser file download
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = downloadName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);

          showSuccess('Export file downloaded successfully');
          onSuccess?.();
          handleCloseModal();
        } else {
          showError('Received invalid file data from server');
          onError?.(response);
        }
      }
    } catch (err: any) {
      console.error('DB Export Error:', err);
      let errorMsg = 'Failed to export data';
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.message || parsed.error || errorMsg;
        } catch {
          // fallback
        }
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      showError(errorMsg);
      onError?.(err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      {showButton && (
        <button
          type="button"
          onClick={handleOpenModal}
          disabled={disabled}
          className={
            buttonClassName ||
            'flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-gray-800 dark:text-white font-bold hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm transition-colors duration-200'}
          title={isFormatOnlyMode ? (effectiveTableName ? `Export ${effectiveTableName}` : `Export ${effectiveDatabaseName || 'database'}`) : 'Export data to Excel or CSV'}
        >
          {buttonIcon || (
            selectedFormat === 'csv' ? (
              <Download className="w-4 h-4 text-gray-700 dark:text-cyan-500" />
            ) : (
              <Download className="w-4 h-4 text-gray-700 dark:text-cyan-500" />
            )
          )}
          <span>{buttonText}</span>
        </button>
      )}

      {/* Modal Dialog */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[51] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-xs p-4 animate-in fade-in duration-150">
            <div
              className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl w-full ${effectiveMaxWidth} overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150`}
            >
              {/* Header */}
              <div className="px-6 py-4.5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/70 dark:bg-gray-900/80">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-xl bg-orange-100 dark:bg-orange-950/60 text-orange-600 dark:text-orange-400 border border-orange-200/50 dark:border-orange-800/40 shadow-xs shrink-0">
                    {selectedFormat === 'csv' ? (
                      <FileText className="w-5 h-5" />
                    ) : (
                      <FileSpreadsheet className="w-5 h-5" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate">
                      <span className="truncate">{modalTitle}</span>
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 shrink-0">
                        {selectedFormat}
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {modalSubtitle}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isExporting}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 cursor-pointer shrink-0 ml-2"
                  title="Close modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
                {/* Mode 1: Format-only export (No columns or sort, only format choice) */}
                {isFormatOnlyMode ? (
                  <>
                    {(effectiveTableName || effectiveDatabaseName) && (
                      <div className="flex items-center gap-3 p-3.5 rounded-xl bg-orange-50/70 dark:bg-orange-950/30 border border-orange-200/80 dark:border-orange-800/40">
                        <div className="p-2 rounded-lg bg-orange-500 text-white shrink-0 shadow-xs">
                          {effectiveTableName ? (
                            <FileSpreadsheet className="w-4 h-4" />
                          ) : (
                            <Database className="w-4 h-4" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600 dark:text-orange-400 block">
                            {effectiveTableName ? 'Target Table' : 'Target Database'}
                          </span>
                          <span className="text-sm font-bold text-gray-900 dark:text-white font-mono truncate block">
                            {effectiveTableName
                              ? effectiveSchemaName
                                ? `${effectiveSchemaName}.${effectiveTableName}`
                                : effectiveTableName
                              : effectiveDatabaseName}
                          </span>
                          {effectiveTableName && effectiveDatabaseName && (
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono block mt-0.5">
                              Database: {effectiveDatabaseName}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-bold text-gray-800 dark:text-gray-200 block mb-2">
                        Select Export Format
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setSelectedFormat('excel')}
                          className={`flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer ${selectedFormat === 'excel'
                              ? 'bg-orange-50/90 dark:bg-orange-950/50 border-orange-500 text-orange-950 dark:text-orange-100 shadow-xs ring-1 ring-orange-500'
                              : 'bg-gray-50/60 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <div
                            className={`p-2 rounded-lg shrink-0 ${selectedFormat === 'excel'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            <FileSpreadsheet className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold">Excel (.xlsx)</span>
                              {selectedFormat === 'excel' && (
                                <div className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 stroke-[3]" />
                                </div>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              Spreadsheet workbook
                            </p>
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedFormat('csv')}
                          className={`flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer ${selectedFormat === 'csv'
                              ? 'bg-orange-50/90 dark:bg-orange-950/50 border-orange-500 text-orange-950 dark:text-orange-100 shadow-xs ring-1 ring-orange-500'
                              : 'bg-gray-50/60 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <div
                            className={`p-2 rounded-lg shrink-0 ${selectedFormat === 'csv'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            <FileText className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold">CSV (.csv)</span>
                              {selectedFormat === 'csv' && (
                                <div className="w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 stroke-[3]" />
                                </div>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              Comma-separated text
                            </p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Mode 2: Standard table export (Format, Columns, Sort, Direction) */
                  <>
                    {/* Format Selector: Excel vs CSV */}
                    <div>
                      <label className="text-xs font-bold text-gray-800 dark:text-gray-200 block mb-2">
                        Export Format
                      </label>
                      <div className="grid grid-cols-2 gap-2.5">
                        <button
                          type="button"
                          onClick={() => setSelectedFormat('excel')}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition-all cursor-pointer ${selectedFormat === 'excel'
                              ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-500 text-orange-700 dark:text-orange-300 font-semibold shadow-xs'
                              : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <FileSpreadsheet className="w-4 h-4 text-orange-500" />
                          <span>Excel (.xlsx)</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedFormat('csv')}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition-all cursor-pointer ${selectedFormat === 'csv'
                              ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-500 text-orange-700 dark:text-orange-300 font-semibold shadow-xs'
                              : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <FileText className="w-4 h-4 text-orange-500" />
                          <span>CSV (.csv)</span>
                        </button>
                      </div>
                    </div>

                    {/* 1. Columns Included in Export */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                          <FileSpreadsheet className="w-3.5 h-3.5 text-orange-500" />
                          <span>Export Columns</span>
                          <span className="text-[11px] font-normal text-gray-500">
                            ({selectedExportColumns.length}/{normalizedColumns.length} selected)
                          </span>
                        </label>
                        <div className="flex items-center gap-2">
                          {selectedExportColumns.length < normalizedColumns.length && (
                            <button
                              type="button"
                              onClick={selectAllColumns}
                              className="text-[11px] text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 hover:underline cursor-pointer font-medium"
                            >
                              Select All
                            </button>
                          )}
                          {selectedExportColumns.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedExportColumns([sortBy || normalizedColumns[0]?.key])
                              }
                              className="text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer font-medium"
                            >
                              Deselect Others
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 max-h-32 overflow-y-auto custom-scrollbar">
                        {normalizedColumns.map((col) => {
                          const isIncluded = selectedExportColumns.includes(col.key);
                          return (
                            <button
                              key={col.key}
                              type="button"
                              onClick={() => toggleExportColumn(col.key)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-all cursor-pointer ${isIncluded
                                  ? 'bg-orange-100/70 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border border-orange-300/60 dark:border-orange-700/60 font-medium'
                                  : 'bg-gray-200/50 dark:bg-gray-700/30 text-gray-400 dark:text-gray-500 border border-transparent hover:bg-gray-200/80 dark:hover:bg-gray-700/60'
                                }`}
                            >
                              {isIncluded ? (
                                <CheckSquare className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                              ) : (
                                <Square className="w-3 h-3 text-gray-400" />
                              )}
                              <span>{col.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* 2. Sort By Column (Compact cards & only selected export columns) */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                          <ArrowUpDown className="w-3.5 h-3.5 text-orange-500" />
                          <span>Sort By Column</span>
                          <span className="text-rose-500">*</span>
                        </label>
                      </div>

                      {selectableSortColumns.length > 6 && (
                        <div className="relative mb-2">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={columnSearch}
                            onChange={(e) => setColumnSearch(e.target.value)}
                            placeholder="Filter sort columns..."
                            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 transition-all"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 max-h-48 overflow-y-auto p-0.5 custom-scrollbar">
                        {selectableSortColumns.map((col) => {
                          const isSelected = sortBy === col.key;
                          return (
                            <button
                              key={col.key}
                              type="button"
                              onClick={() => setSortBy(col.key)}
                              className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-left transition-all cursor-pointer ${isSelected
                                  ? 'bg-orange-50/90 dark:bg-orange-950/50 border-orange-500 text-orange-900 dark:text-orange-200 font-semibold shadow-xs'
                                  : 'bg-gray-50/70 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                              <span className="text-xs truncate font-medium">{col.label}</span>
                              {isSelected && (
                                <div className="w-3.5 h-3.5 rounded-full bg-orange-500 text-white flex items-center justify-center shrink-0 ml-1.5 shadow-xs">
                                  <Check className="w-2 h-2 stroke-[3]" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {selectableSortColumns.length === 0 && (
                        <div className="py-4 text-center text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/40 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                          No matching columns found
                        </div>
                      )}
                    </div>

                    {/* 3. Sort Direction */}
                    <div>
                      <label className="text-xs font-bold text-gray-800 dark:text-gray-200 block mb-2">
                        Sort Direction
                      </label>
                      <div className="grid grid-cols-2 gap-2.5">
                        <button
                          type="button"
                          onClick={() => setSortOrder('desc')}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition-all cursor-pointer ${sortOrder === 'desc'
                              ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-500 text-orange-700 dark:text-orange-300 font-semibold shadow-xs'
                              : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <ArrowDownWideNarrow className="w-4 h-4 text-orange-500" />
                          <span>Descending (High to Low)</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setSortOrder('asc')}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition-all cursor-pointer ${sortOrder === 'asc'
                              ? 'bg-orange-50 dark:bg-orange-950/40 border-orange-500 text-orange-700 dark:text-orange-300 font-semibold shadow-xs'
                              : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                        >
                          <ArrowUpNarrowWide className="w-4 h-4 text-orange-500" />
                          <span>Ascending (Low to High)</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/80 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isExporting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-200/60 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleExport}
                  disabled={isExporting || (!isFormatOnlyMode && selectedExportColumns.length === 0)}
                  className="flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-xl bg-orange-600 hover:bg-orange-500 text-white transition-all shadow-md shadow-orange-600/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Export {selectedFormat.toUpperCase()}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// Aliases for intuitive imports
export const DBExportButton = DBExportModal;
export default DBExportModal;
